/**
 * manager-splide.js
 * Inicializa instâncias do Splide a partir de data attributes
 * e sincroniza o autoplay de vídeos por slide.
 */

class SplideManager {
  constructor() {
    this.selector = '[data-splide-component]';
    this.retryDelay = 80;
    this.retryLimit = 40;

    this._handleSectionLoad = this._handleSectionLoad.bind(this);
    this._handleSectionUnload = this._handleSectionUnload.bind(this);
    this._handleBlockSelect = this._handleBlockSelect.bind(this);

    this._bindEvents();
    this._boot();
  }

  _boot() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
      return;
    }

    this.init();
  }

  _bindEvents() {
    document.addEventListener('shopify:section:load', this._handleSectionLoad);
    document.addEventListener('shopify:section:unload', this._handleSectionUnload);
    document.addEventListener('shopify:block:select', this._handleBlockSelect);
  }

  _handleSectionLoad(event) {
    this.init(event.target);
  }

  _handleSectionUnload(event) {
    this.destroy(event.target);
  }

  _handleBlockSelect(event) {
    const blockId = event.detail?.blockId;
    const slide = blockId
      ? document.querySelector(`[data-splide-block-id="${blockId}"]`)
      : event.target?.closest?.('[data-splide-slide-index]');
    if (!slide) return;

    const root = slide.closest(this.selector);
    if (!root?.splide) return;

    const index = Number(slide.dataset.splideSlideIndex || 0);
    root.splide.go(index);
    this._syncSlideVideos(root.splide);
  }

  init(scope = document, attempt = 0) {
    const roots = scope.matches?.(this.selector)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(this.selector) || []);

    if (!roots.length) return;

    if (typeof window.Splide === 'undefined') {
      if (attempt >= this.retryLimit) return;
      window.setTimeout(() => this.init(scope, attempt + 1), this.retryDelay);
      return;
    }

    roots.forEach((root) => this._mount(root));
  }

  destroy(scope = document) {
    const roots = scope.matches?.(this.selector)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(this.selector) || []);

    roots.forEach((root) => {
      if (!root.splide) return;
      root.splide.destroy();
      delete root.splide;
    });
  }

  _mount(root) {
    if (!root) return;

    const slides = root.querySelectorAll('.splide__slide:not(.splide__slide--clone)');
    if (!slides.length) return;

    if (root.splide) {
      root.splide.destroy();
      delete root.splide;
    }

    const options = this._getOptions(root);
    const instance = new window.Splide(root, options);

    instance.on('mounted', () => {
      this._updateProductSliderArrowPosition(instance);
      this._updateCounter(instance);
      this._primeNearbyVideos(instance);
      this._syncSlideVideos(instance);
      this._ensureInitialVideoPlayback(instance);
      this._initInstagramVideos(instance);
    });

    instance.on('move', (_newIndex) => {
      this._primeNearbyVideos(instance, _newIndex);
    });

    instance.on('moved', () => {
      this._updateCounter(instance);
      this._syncSlideVideos(instance);
      this._handleInstagramVideosMoved(instance);
    });

    instance.on('resized refresh updated', () => {
      this._clearStaleSlideWidth(instance);
      this._updateProductSliderArrowPosition(instance);
      this._updateCounter(instance);
      this._primeNearbyVideos(instance);
      this._syncSlideVideos(instance);
      this._syncInstagramVideos(instance);
    });

    instance.on('destroy', () => {
      this._pauseAllVideos(root);
      this._pauseInstagramVideos(root);
    });

    instance.mount();
    root.splide = instance;
  }

  _updateProductSliderArrowPosition(instance) {
    const root = instance.root;
    if (!root.matches('.productSlider__root')) return;

    const media = root.querySelector('.productCard__media');
    if (!media) return;

    const rootRect = root.getBoundingClientRect();
    const mediaRect = media.getBoundingClientRect();
    if (!mediaRect.height) return;

    const centerY = mediaRect.top - rootRect.top + mediaRect.height / 2;
    root.style.setProperty('--product-slider-arrow-center-y', `${centerY}px`);
  }

  _getOptions(root) {
    const counterType = root.dataset.splideCounterType || 'dots';
    const arrows = this._toBoolean(root.dataset.splideArrows, true);
    const showArrowsMobile = this._toBoolean(root.dataset.splideArrowsMobile, false);
    const autoplay = this._toBoolean(root.dataset.splideAutoplay, true);
    const autoWidth = this._toBoolean(root.dataset.splideAutoWidth, false);
    const trimSpace = this._toBoolean(root.dataset.splideTrimSpace, true);
    const focus = root.dataset.splideFocus;
    const perPage = this._toNumber(root.dataset.splidePerPage, 1);
    const perMove = this._toNumber(root.dataset.splidePerMove, 1);
    const perPageMobile = this._toNumber(root.dataset.splidePerPageMobile, perPage);
    const perMoveMobile = this._toNumber(root.dataset.splidePerMoveMobile, perMove);
    const gap = root.dataset.splideGap || undefined;
    const gapMobile = root.dataset.splideGapMobile || gap;
    const autoWidthMobile = this._toBoolean(root.dataset.splideAutoWidthMobile, autoWidth);
    const trimSpaceMobile = this._toBoolean(root.dataset.splideTrimSpaceMobile, trimSpace);
    const fixedWidth = root.dataset.splideFixedWidth || undefined;
    const fixedWidthMobile = root.dataset.splideFixedWidthMobile || fixedWidth;
    const focusMobile = root.dataset.splideFocusMobile;
    const resolvedFocus = focus === undefined
      ? undefined
      : (focus === 'center' ? 'center' : this._toNumber(focus, 0));
    const resolvedFocusMobile = focusMobile === undefined
      ? resolvedFocus
      : (focusMobile === 'center' ? 'center' : this._toNumber(focusMobile, 0));

    return {
      type: root.dataset.splideType || 'fade',
      perPage,
      perMove,
      speed: this._toNumber(root.dataset.splideSpeed, 900),
      interval: this._toNumber(root.dataset.splideInterval, 5000),
      autoplay,
      pauseOnHover: this._toBoolean(root.dataset.splidePauseOnHover, true),
      pauseOnFocus: this._toBoolean(root.dataset.splidePauseOnFocus, true),
      rewind: this._toBoolean(root.dataset.splideRewind, true),
      arrows,
      pagination: counterType === 'dots' || counterType === 'stroke',
      drag: this._toBoolean(root.dataset.splideDrag, true),
      keyboard: this._toBoolean(root.dataset.splideKeyboard, true) ? 'focused' : false,
      lazyLoad: false,
      autoWidth,
      trimSpace,
      focus: resolvedFocus,
      fixedWidth,
      gap,
      breakpoints: {
        989: {
          autoWidth: autoWidthMobile,
          trimSpace: trimSpaceMobile,
          fixedWidth: fixedWidthMobile,
          focus: resolvedFocusMobile,
          perPage: perPageMobile,
          perMove: perMoveMobile,
          gap: gapMobile,
        },
        749: {
          arrows: arrows && showArrowsMobile,
        },
      },
    };
  }

  /**
   * O Splide só grava `width` inline nos slides quando `autoWidth` e falso.
   * Quando o breakpoint muda com a janela sendo redimensionada, ele apenas
   * deixa de escrever a largura nova, mas nao limpa a antiga: os slides ficam
   * presos no tamanho do breakpoint anterior ate um reload da pagina.
   * Removemos o inline para o CSS do slide voltar a mandar na largura.
   */
  _clearStaleSlideWidth(instance) {
    if (!instance.options?.autoWidth) return;

    let cleared = false;

    instance.root.querySelectorAll('.splide__slide').forEach((slide) => {
      if (!slide.style.width) return;
      slide.style.width = '';
      cleared = true;
    });

    if (cleared) instance.Components?.Layout?.resize(true);
  }

  _updateCounter(instance) {
    const root = instance.root;
    const current = this._findCounterNode(root, '[data-splide-current]');
    const total = this._findCounterNode(root, '[data-splide-total]');
    const progress = this._findCounterNode(root, '[data-splide-progress]');
    const progressMode = root.dataset.splideProgressMode || 'slide';

    if (total) {
      total.textContent = String(instance.length);
    }

    if (current) {
      current.textContent = String(instance.index + 1);
    }

    if (progress) {
      const width = progressMode === 'viewport'
        ? this._getViewportProgress(instance)
        : progressMode === 'page'
          ? this._getPageProgress(instance)
        : this._getSlideProgress(instance);

      progress.style.width = `${width}%`;
    }
  }

  _getSlideProgress(instance) {
    if (instance.length <= 0) return 0;
    return ((instance.index + 1) / instance.length) * 100;
  }

  _getViewportProgress(instance) {
    const track = instance.root.querySelector('.splide__track');
    const list = instance.root.querySelector('.splide__list');

    if (!track || !list || list.scrollWidth <= 0) return 100;

    const maxOffset = Math.max(list.scrollWidth - track.clientWidth, 0);
    if (maxOffset <= 0) return 100;

    const position = Math.max(instance.Components.Move.getPosition(), -maxOffset);
    const viewedWidth = Math.min(Math.abs(position) + track.clientWidth, list.scrollWidth);

    return (viewedWidth / list.scrollWidth) * 100;
  }

  _getPageProgress(instance) {
    const endIndex = instance.Components.Controller.getEnd() + 1;
    if (endIndex <= 1) return 100;

    const pageIndex = Math.min(instance.index, endIndex - 1);
    return ((pageIndex + 1) / endIndex) * 100;
  }

  _findCounterNode(root, selector) {
    return root.querySelector(selector)
      || root.parentElement?.querySelector(selector)
      || root.closest('section, .shopify-section')?.querySelector(selector)
      || null;
  }

  _isInstagramVideos(instance) {
    return instance?.root?.dataset.splideComponent === 'instagram-videos-slider';
  }

  _initInstagramVideos(instance) {
    if (!this._isInstagramVideos(instance)) return;

    const root = instance.root;
    root.splide = instance;

    if (root.dataset.instagramManagerBound !== 'true') {
      root.dataset.instagramManagerBound = 'true';
      root.addEventListener('click', (event) => {
        this._handleInstagramVideoClick(root, event);
      }, true);
      root.addEventListener('ended', (event) => {
        this._handleInstagramVideoEnded(root, event);
      }, true);
      root.addEventListener('play', (event) => {
        this._syncInstagramVideoButton(event.target);
      }, true);
      root.addEventListener('pause', (event) => {
        this._syncInstagramVideoButton(event.target);
      }, true);
      root.addEventListener('volumechange', (event) => {
        this._syncInstagramVideoButton(event.target);
      }, true);
    }

    this._syncInstagramVideos(instance);
    window.requestAnimationFrame(() => this._syncInstagramVideos(instance));
    window.setTimeout(() => this._syncInstagramVideos(instance), 240);
  }

  _handleInstagramVideoClick(root, event) {
    const toggleButton = event.target.closest?.('[data-instagram-add-toggle]');
    if (toggleButton && root.contains(toggleButton)) {
      if (toggleButton.disabled) return;

      event.preventDefault();
      event.stopPropagation();

      const media = toggleButton.closest('[data-instagram-media]');
      const popup = media?.querySelector('[data-instagram-size-popup]');
      if (!popup) return;

      document.querySelectorAll('[data-instagram-size-popup]').forEach((otherPopup) => {
        if (otherPopup !== popup) {
          otherPopup.hidden = true;
          otherPopup.closest('[data-instagram-media]')
            ?.querySelector('[data-instagram-add-toggle]')
            ?.setAttribute('aria-expanded', 'false');
        }
      });

      const isOpen = popup.hidden;
      popup.hidden = !isOpen;
      toggleButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      return;
    }

    const sizeOption = event.target.closest?.('[data-instagram-size-popup] [data-instagram-size-option]');
    if (sizeOption && root.contains(sizeOption)) {
      if (sizeOption.disabled) return;

      event.preventDefault();
      event.stopPropagation();
      const media = sizeOption.closest('[data-instagram-media]');
      const button = media?.querySelector('[data-instagram-add-toggle]');
      sizeOption.closest('[data-instagram-size-popup]').hidden = true;
      button?.setAttribute('aria-expanded', 'false');
      window.CartManager?.add(sizeOption.dataset.variantId, {
        button,
        action: 'open_drawer',
        variantPrice: sizeOption.dataset.price
      });
      return;
    }

    if (!event.target.closest('[data-instagram-size-popup]')) {
      root.querySelectorAll('[data-instagram-size-popup]').forEach((popup) => {
        popup.hidden = true;
        popup.closest('[data-instagram-media]')
          ?.querySelector('[data-instagram-add-toggle]')
          ?.setAttribute('aria-expanded', 'false');
      });
    }

    const playButton = event.target.closest?.('[data-instagram-toggle-play]');
    const volumeButton = event.target.closest?.('[data-instagram-toggle-volume]');
    const button = playButton || volumeButton;
    if (!button || !root.contains(button)) return;

    const media = button.closest('[data-instagram-media]');
    const video = media?.querySelector('[data-instagram-video]');
    if (!video) return;

    event.preventDefault();
    event.stopPropagation();

    if (playButton) {
      if (video.paused) {
        video.dataset.userPaused = 'false';
        root.__instagramManualVideo = video;
        this._playInstagramVideo(root, video);
      } else {
        video.dataset.userPaused = 'true';
        this._pauseVideo(video, false);
        this._syncInstagramVideoButton(video);
      }
      return;
    }

    video.muted = !video.muted;
    this._syncInstagramVideoButton(video);
  }

  _handleInstagramVideoEnded(root, event) {
    const video = event.target;
    if (!video.matches?.('[data-instagram-video]')) return;

    const activeVideo = this._getActiveInstagramVideo(root);
    if (video !== activeVideo) return;

    const instance = root.splide;
    if (!instance || instance.length < 1) return;

    const slide = video.closest('.splide__slide');
    const currentIndex = Number(slide?.dataset.splideSlideIndex ?? instance.index);
    const nextIndex = this._normalizeIndex(currentIndex + 1, instance.length);

    root.__instagramManualVideo = null;
    video.dataset.userPaused = 'false';

    if (nextIndex === instance.index) {
      video.currentTime = 0;
      this._playInstagramVideo(root, video);
      return;
    }

    root.__instagramAutoAdvance = true;
    root.__instagramAutoAdvanceIndex = nextIndex;
    instance.go(nextIndex);
    this._scheduleInstagramVideoAutoplay(instance, nextIndex);
  }


  _prepareInstagramVideoForAutoplay(video) {
    video.autoplay = true;
    video.defaultMuted = true;
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.dataset.userPaused = 'false';
    video.dataset.instagramPlayRequested = 'true';
  }

  _getInstagramVideoForIndex(instance, targetIndex) {
    const expectedIndex = this._normalizeIndex(targetIndex, instance.length);
    const slides = instance.Components?.Slides?.get() || [];
    let bestVideo = null;
    let bestScore = -1;

    slides.forEach((slideObject) => {
      const logicalIndex = slideObject.isClone
        ? this._normalizeIndex(slideObject.slideIndex, instance.length)
        : this._normalizeIndex(slideObject.index, instance.length);

      if (logicalIndex !== expectedIndex) return;

      const slide = slideObject.slide;
      const video = slide?.querySelector?.('[data-instagram-video]');
      if (!video) return;

      let score = 0;
      if (slide.classList.contains('is-active')) score += 8;
      if (slide.classList.contains('is-visible')) score += 4;
      if (this._isVisible(slide)) score += 2;
      if (this._isVisible(video)) score += 1;

      if (score > bestScore) {
        bestScore = score;
        bestVideo = video;
      }
    });

    return bestVideo;
  }

  _getActiveInstagramVideoAtIndex(instance, targetIndex) {
    const currentIndex = this._normalizeIndex(instance.index, instance.length);
    const expectedIndex = this._normalizeIndex(targetIndex, instance.length);
    if (currentIndex !== expectedIndex) return null;

    return this._getInstagramVideoForIndex(instance, expectedIndex);
  }

  _scheduleInstagramVideoAutoplay(instance, targetIndex) {
    const root = instance.root;
    const speed = Number(instance.options?.speed || 700);
    let didResetTime = false;

    const playTargetVideo = () => {
      const video = this._getActiveInstagramVideoAtIndex(instance, targetIndex);
      if (!video) return;

      this._prepareInstagramVideoForAutoplay(video);

      if (!didResetTime) {
        didResetTime = true;
        try {
          video.currentTime = 0;
        } catch (error) {
          /* noop */
        }
      }

      if (video.paused) this._playInstagramVideo(root, video);
    };

    window.setTimeout(playTargetVideo, speed + 80);
    window.setTimeout(playTargetVideo, speed + 480);
    window.setTimeout(playTargetVideo, speed + 1080);
  }
  _handleInstagramVideosMoved(instance) {
    if (!this._isInstagramVideos(instance)) return;

    const root = instance.root;
    if (root.__instagramAutoAdvance !== true) {
      this._syncInstagramVideos(instance);
      return;
    }

    root.__instagramAutoAdvance = false;
    const targetIndex = this._normalizeIndex(
      Number(root.__instagramAutoAdvanceIndex ?? instance.index),
      instance.length
    );

    const playActiveVideo = (resetTime = false) => {
      const activeVideo = this._getActiveInstagramVideoAtIndex(instance, targetIndex);
      if (!activeVideo) return;

      this._prepareInstagramVideoForAutoplay(activeVideo);

      if (resetTime) {
        try {
          activeVideo.currentTime = 0;
        } catch (error) {
          /* noop */
        }
      }

      this._playInstagramVideo(root, activeVideo);
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => playActiveVideo(true));

      window.setTimeout(() => {
        const activeVideo = this._getActiveInstagramVideoAtIndex(instance, targetIndex);
        if (activeVideo?.paused) playActiveVideo(false);
      }, 180);

      window.setTimeout(() => {
        const activeVideo = this._getActiveInstagramVideoAtIndex(instance, targetIndex);
        if (activeVideo?.paused) playActiveVideo(false);
      }, 600);
    });
  }

  _getActiveInstagramVideo(root) {
    if (root.__instagramManualVideo?.isConnected) {
      return root.__instagramManualVideo;
    }

    const instance = root.splide;
    if (instance) {
      const activeVideo = this._getInstagramVideoForIndex(instance, instance.index);
      if (activeVideo) return activeVideo;
    }

    return root.querySelector('.splide__slide.is-active [data-instagram-video]')
      || root.querySelector('.splide__slide:not(.splide__slide--clone) [data-instagram-video]');
  }

  _syncInstagramVideos(instance) {
    if (!this._isInstagramVideos(instance)) return;

    const root = instance.root;
    const activeVideo = this._getActiveInstagramVideo(root);
    if (!activeVideo) return;

    this._pauseInstagramVideos(root, activeVideo);

    if (activeVideo.dataset.userPaused === 'true') {
      this._syncInstagramVideoButton(activeVideo);
      return;
    }

    this._playInstagramVideo(root, activeVideo);
  }

  _playInstagramVideo(root, video) {
    this._pauseInstagramVideos(root, video);
    video.dataset.instagramPlayRequested = 'true';
    video.dataset.instagramPlayPending = 'false';
    video.preload = 'auto';

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise
        .then(() => this._syncInstagramVideoButton(video))
        .catch(() => this._syncInstagramVideoButton(video));
    }

    this._syncInstagramVideoButton(video);
  }

  _pauseInstagramVideos(root, exceptVideo = null) {
    root.querySelectorAll('[data-instagram-video]').forEach((video) => {
      if (video === exceptVideo) return;
      video.dataset.instagramPlayRequested = 'false';
      video.dataset.instagramPlayPending = 'false';
      this._pauseVideo(video, false);
      this._syncInstagramVideoButton(video);
    });
  }

  _syncInstagramVideoButton(video) {
    if (!video?.matches?.('[data-instagram-video]')) return;

    const media = video.closest('[data-instagram-media]');
    const playButton = media?.querySelector('[data-instagram-toggle-play]');
    const volumeButton = media?.querySelector('[data-instagram-toggle-volume]');

    if (playButton) {
      const isPlaying = !video.paused;
      playButton.dataset.state = isPlaying ? 'playing' : 'paused';
      playButton.setAttribute('aria-label', isPlaying ? 'Pausar video' : 'Reproduzir video');
    }

    if (volumeButton) {
      volumeButton.dataset.state = video.muted ? 'muted' : 'unmuted';
      volumeButton.setAttribute('aria-label', video.muted ? 'Ativar som' : 'Desativar som');
    }
  }
  _syncSlideVideos(instance) {
    const slides = Array.from(
      instance.root.querySelectorAll('.splide__slide:not(.splide__slide--clone)')
    );

    slides.forEach((slide) => {
      const videos = Array.from(slide.querySelectorAll('[data-splide-video]'));

      videos.forEach((video) => {
        if (this._isVisible(video)) {
          this._hydrateVideo(video);
          this._playVideo(video);
        } else {
          this._pauseVideo(video, false);
        }
      });
    });
  }

  _primeNearbyVideos(instance, targetIndex = instance.index) {
    const slides = Array.from(
      instance.root.querySelectorAll('.splide__slide:not(.splide__slide--clone)')
    );

    if (!slides.length) return;

    const indexes = slides.map((_, index) => index);

    indexes.forEach((index) => {
      const slide = slides[index];
      if (!slide) return;

      slide.querySelectorAll('[data-splide-video]').forEach((video) => {
        if (!this._isVisible(video)) return;
        this._hydrateVideo(video);
      });
    });
  }

  _pauseAllVideos(root) {
    root.querySelectorAll('[data-splide-video]').forEach((video) => {
      this._pauseVideo(video, false);
    });
  }

  _ensureInitialVideoPlayback(instance) {
    const rerun = () => {
      this._primeNearbyVideos(instance);
      this._syncSlideVideos(instance);
    };

    window.requestAnimationFrame(rerun);
    window.setTimeout(rerun, 160);
    window.setTimeout(rerun, 480);
  }

  _hydrateVideo(video) {
    if (video.dataset.videoHydrated === 'true') return;

    const poster = video.dataset.poster;
    if (poster) {
      video.setAttribute('poster', poster);
    }

    const sources = video.querySelectorAll('source[data-src]');
    sources.forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });

    const directSrc = video.dataset.src;
    if (directSrc) {
      video.src = directSrc;
      video.removeAttribute('data-src');
    }

    video.load();
    video.dataset.videoHydrated = 'true';
  }

  _playVideo(video) {
    if (video.dataset.videoPlayPending === 'true') return;

    if (video.readyState < 2) {
      video.dataset.videoPlayPending = 'true';

      const onReady = () => {
        video.removeEventListener('loadeddata', onReady);
        video.removeEventListener('canplay', onReady);
        video.dataset.videoPlayPending = 'false';
        this._playVideo(video);
      };

      video.addEventListener('loadeddata', onReady, { once: true });
      video.addEventListener('canplay', onReady, { once: true });
      return;
    }

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }

  _pauseVideo(video, resetTime = false) {
    video.dataset.videoPlayPending = 'false';
    video.pause();

    if (!resetTime) return;

    try {
      video.currentTime = 0;
    } catch (error) {
      /* noop */
    }
  }


  _isVisible(element) {
    if (!element) return false;

    if (typeof element.checkVisibility === 'function') {
      return element.checkVisibility({
        checkOpacity: false,
        checkVisibilityCSS: true,
      });
    }

    return Boolean(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
  }

  _normalizeIndex(index, length) {
    if (length <= 0) return 0;
    return ((index % length) + length) % length;
  }

  _toBoolean(value, fallback) {
    if (value === undefined) return fallback;
    return value === 'true';
  }

  _toNumber(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }
}

window.SplideManager = new SplideManager();
